import { CloudOff, RotateCcw } from "lucide-react";
import { formatRelativeTime } from "@/lib/useLocalDraft";

/**
 * UI helpers for the persistent local-draft hook (`useLocalDraft`).
 * Pass the `draft` object returned by the hook plus the form `setForm` setter
 * (so that `restore()` can apply the saved state).
 *
 * `kind` is used to pick the banner copy ("Bozza locale disponibile") and to
 * scope `data-testid` attributes (e.g. "quote", "proforma", "inventory").
 */
export function LocalDraftBanner({ draft, onRestore, kind = "doc" }) {
  if (!draft.pendingRestore) return null;
  return (
    <div
      data-testid={`${kind}-draft-restore-banner`}
      className="rounded-sm border border-amber-300 bg-amber-50 px-3 sm:px-4 py-3 flex items-start gap-3"
    >
      <CloudOff className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-medium text-amber-900">Bozza locale disponibile</div>
        <div className="text-xs text-amber-700 mt-0.5">
          Hai modifiche non salvate sul server (salvate {formatRelativeTime(draft.pendingRestore.savedAt)}). Vuoi ripristinarle?
        </div>
      </div>
      <div className="flex gap-2 shrink-0 flex-wrap">
        <button
          type="button"
          onClick={onRestore}
          data-testid={`${kind}-draft-restore-btn`}
          className="inline-flex items-center gap-1 h-8 px-3 text-xs font-medium rounded-sm bg-amber-600 text-white hover:bg-amber-700"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Ripristina
        </button>
        <button
          type="button"
          onClick={draft.dismiss}
          data-testid={`${kind}-draft-dismiss-btn`}
          className="h-8 px-3 text-xs font-medium rounded-sm border border-amber-300 text-amber-800 hover:bg-amber-100"
        >
          Scarta
        </button>
      </div>
    </div>
  );
}

export function LocalDraftSavedIndicator({ draft, kind = "doc" }) {
  if (draft.pendingRestore || !draft.savedAt) return null;
  return (
    <div
      data-testid={`${kind}-draft-saved-indicator`}
      className="flex items-center gap-1.5 text-[11px] text-zinc-500 -mt-1"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      <span>Salvato localmente · {formatRelativeTime(draft.savedAt)}</span>
    </div>
  );
}
